// =====================================================================================
// useEditModePhaser - Phaser scene bridge for decoration edit mode
// =====================================================================================
// Manages bidirectional communication between React state and Phaser scene events.
// Handles state synchronization, decoration selection, mode changes, and depth adjustment.

import { useEffect, useCallback } from 'react';
import { useGameInstance } from '../contexts/GameInstanceContext.js';
import type { TilemapObject } from '@shared/schemas/decorations.schema.js';
import type { DecorationConfig } from '../game/systems/decorations/types.js';
import type { EditMode, DecorationItem } from '../contexts/editMode.types.js';
import type { SpriteState } from './useEditModeHistory.js';

/** Build a TilemapObject snapshot from a stored decoration's current sprite state */
function buildTilemapSnapshot(
  config: { id?: string; gid: number; x: number; y: number; width: number; height: number; rotation?: number },
  sprite: { x: number; y: number; depth: number }
): { objectId: number; oldObject: TilemapObject; newObject: TilemapObject } | null {
  const objectId = config.id ? parseInt(config.id, 10) : NaN;
  if (isNaN(objectId)) return null;

  // oldObject uses config (original Tiled coordinates, before any edits)
  const oldObject: TilemapObject = {
    id: objectId,
    gid: config.gid,
    x: config.x,
    y: config.y,
    width: config.width,
    height: config.height,
    visible: true,
  };

  // newObject uses current sprite state (reflects any pending moves/depth changes)
  const newObject: TilemapObject = {
    id: objectId,
    gid: config.gid,
    x: sprite.x - config.width / 2, // Sprite center → Tiled left edge
    y: sprite.y,                     // Sprite bottom = Tiled Y (origin 0.5, 1)
    width: config.width,
    height: config.height,
    visible: true,
    properties: [
      { name: 'depth', type: 'float', value: sprite.depth }
    ],
  };

  return { objectId, oldObject, newObject };
}

/** Undo data passed alongside remove operations */
export interface RemoveUndoData {
  decorationConfig?: DecorationConfig;
  spriteState?: SpriteState;
}

/**
 * Hook that bridges React edit mode state with the Phaser game scene.
 * Emits state changes to Phaser and listens for scene events (place/remove/select/move).
 */
export function useEditModePhaser(
  isActive: boolean,
  mode: EditMode,
  selectedDecoration: DecorationItem | null,
  setMode: React.Dispatch<React.SetStateAction<EditMode>>,
  setSelectedDecoration: React.Dispatch<React.SetStateAction<DecorationItem | null>>,
  setSelectedPlacedDecorationId: React.Dispatch<React.SetStateAction<string | null>>,
  addDecoration: (x: number, y: number, objectId?: number) => void,
  removeDecoration: (objectId: number, undoData?: RemoveUndoData) => void,
  updateDecoration: (objectId: number, oldObject: TilemapObject, newObject: TilemapObject, preSpriteState?: SpriteState) => void
): {
  selectDecoration: (decoration: DecorationItem | null) => void;
  selectPlacedDecoration: (decorationId: string | null) => void;
  changeMode: (mode: EditMode) => void;
  adjustDecorationDepth: (decorationId: string, direction: 'up' | 'down') => void;
} {
  const { getGameScene } = useGameInstance();

  // Emit edit mode state changes to Phaser for ghost preview and input handling
  useEffect(() => {
    const scene = getGameScene();
    if (scene) {
      scene.events.emit('editMode:stateChanged', {
        isActive,
        mode,
        selectedDecoration
      });
    }
  }, [isActive, mode, selectedDecoration, getGameScene]);

  // Select decoration for placement
  const selectDecoration = useCallback((decoration: DecorationItem | null) => {
    setSelectedDecoration(decoration);
    if (decoration) {
      setMode('place');
      // Notify the game scene about the selected decoration
      const scene = getGameScene();
      if (scene) {
        scene.events.emit('decoration:selected', decoration);
      }
    } else {
      setMode('select');
      // Clear selection in game scene
      const scene = getGameScene();
      if (scene) {
        scene.events.emit('decoration:selected', null);
      }
    }
  }, [getGameScene, setSelectedDecoration, setMode]);

  // Select a placed decoration in the scene (for future move/inspect)
  const selectPlacedDecoration = useCallback((decorationId: string | null) => {
    setSelectedPlacedDecorationId(decorationId);

    // Update visual selection in Phaser
    const scene = getGameScene();
    if (scene?.systems?.decorations) {
      scene.systems.decorations.selectDecoration(decorationId);
    }
  }, [getGameScene, setSelectedPlacedDecorationId]);

  // Change edit mode and notify Phaser
  const changeMode = useCallback((newMode: EditMode) => {
    setMode(newMode);

    // Clear placed decoration selection when switching away from select mode
    if (newMode !== 'select') {
      setSelectedPlacedDecorationId(null);
    }

    // Immediately notify Phaser of the mode change
    const scene = getGameScene();
    if (scene) {
      scene.events.emit('editMode:stateChanged', {
        isActive,
        mode: newMode,
        selectedDecoration
      });
    }
  }, [isActive, selectedDecoration, getGameScene, setMode, setSelectedPlacedDecorationId]);

  // Adjust decoration depth (z-order) and stage the change
  const adjustDecorationDepth = useCallback((decorationId: string, direction: 'up' | 'down') => {
    const gameScene = getGameScene();
    if (!gameScene?.systems?.decorations) return;

    const decoration = gameScene.systems.decorations.getDecoration(decorationId);
    if (!decoration) return;

    // Capture pre-change sprite state for undo
    const preSpriteState: SpriteState = {
      x: decoration.sprite.x,
      y: decoration.sprite.y,
      depth: decoration.sprite.depth
    };

    // Apply visual depth change
    const adjustment = direction === 'up' ? 0.01 : -0.01;
    decoration.sprite.setDepth(decoration.sprite.depth + adjustment);

    // Stage the update for persistence (with pre-change state for undo)
    const snapshot = buildTilemapSnapshot(decoration.config, decoration.sprite);
    if (snapshot) {
      updateDecoration(snapshot.objectId, snapshot.oldObject, snapshot.newObject, preSpriteState);
    }
  }, [getGameScene, updateDecoration]);

  // Listen for Phaser events to update React state (CRITICAL for Accept button)
  // This MUST be after addDecoration and removeDecoration callbacks are defined
  useEffect(() => {
    const scene = getGameScene();
    if (!scene) return;

    // Handle decoration placement from Phaser
    function handlePlaceDecoration({ x, y, objectId }: { x: number; y: number; objectId?: number }) {
      addDecoration(x, y, objectId);
    }

    // Handle decoration removal from Phaser (includes undo data)
    function handleRemoveDecoration({
      decorationId,
      objectId,
      decorationConfig,
      spriteState
    }: {
      decorationId: string;
      objectId?: number;
      decorationConfig?: DecorationConfig;
      spriteState?: SpriteState;
    }) {
      if (objectId !== undefined) {
        removeDecoration(objectId, { decorationConfig, spriteState });
      } else {
        // Fallback: try to parse the ID from the decorationId string
        const idMatch = decorationId.match(/\d+/);
        if (idMatch) {
          const numericId = parseInt(idMatch[0], 10);
          removeDecoration(numericId, { decorationConfig, spriteState });
        }
      }
    }

    // Handle decoration selection from Phaser (select mode click)
    function handleSelectDecoration({ decorationId }: { decorationId: string | null }) {
      setSelectedPlacedDecorationId(decorationId);
    }

    // Handle decoration move from Phaser (drag-and-drop, includes pre-drag state)
    function handleMoveDecoration({
      decorationId,
      preSpriteState
    }: {
      decorationId: string;
      preSpriteState?: SpriteState;
    }) {
      const decorationSystem = scene.systems?.decorations;
      if (!decorationSystem) return;

      const decoration = decorationSystem.getDecoration(decorationId);
      if (!decoration) return;

      // Build snapshot from current sprite state (post-move)
      const snapshot = buildTilemapSnapshot(decoration.config, decoration.sprite);
      if (snapshot) {
        updateDecoration(snapshot.objectId, snapshot.oldObject, snapshot.newObject, preSpriteState);
      }
    }

    // Listen for events from EditModeInputHandler
    scene.events.on('editMode:placeDecoration', handlePlaceDecoration);
    scene.events.on('editMode:removeDecoration', handleRemoveDecoration);
    scene.events.on('editMode:selectDecoration', handleSelectDecoration);
    scene.events.on('editMode:moveDecoration', handleMoveDecoration);

    // Cleanup listeners on unmount or scene change
    return () => {
      scene.events.off('editMode:placeDecoration', handlePlaceDecoration);
      scene.events.off('editMode:removeDecoration', handleRemoveDecoration);
      scene.events.off('editMode:selectDecoration', handleSelectDecoration);
      scene.events.off('editMode:moveDecoration', handleMoveDecoration);
    };
  }, [getGameScene, addDecoration, removeDecoration, updateDecoration, setSelectedPlacedDecorationId]);

  return {
    selectDecoration,
    selectPlacedDecoration,
    changeMode,
    adjustDecorationDepth
  };
}
